import { useState } from "react";
import { Pencil, Trash2, Check, X } from "lucide-react";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../lib/firebase";

function formatDate(ts) {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function SubmissionsTable({ title, collectionName, columns, rows }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [confirmingId, setConfirmingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rowError, setRowError] = useState({});

  const startEdit = (row) => {
    setConfirmingId(null);
    setEditingId(row.id);
    const initial = {};
    columns.forEach((col) => {
      initial[col.key] = row[col.key];
    });
    setDraft(initial);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const saveEdit = async (id) => {
    setBusyId(id);
    setRowError((e) => ({ ...e, [id]: null }));
    try {
      const payload = { ...draft };
      columns.forEach((col) => {
        if (col.type === "number") payload[col.key] = Number(payload[col.key]);
      });
      await updateDoc(doc(db, collectionName, id), payload);
      setEditingId(null);
      setDraft({});
    } catch (err) {
      setRowError((e) => ({ ...e, [id]: `Save failed: ${err.message}` }));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    setBusyId(id);
    setRowError((e) => ({ ...e, [id]: null }));
    try {
      await deleteDoc(doc(db, collectionName, id));
    } catch (err) {
      setRowError((e) => ({ ...e, [id]: `Delete failed: ${err.message}` }));
      setConfirmingId(null);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-2xl border border-navy/10 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-navy/8 px-5 py-4">
        <h2 className="text-lg font-bold text-navy">{title}</h2>
        <span className="text-sm font-semibold text-navy/40">{rows.length} total</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b border-navy/8 text-xs font-bold tracking-wide text-navy/40 uppercase">
              {columns.map((col) => (
                <th key={col.key} className="px-5 py-3">
                  {col.label}
                </th>
              ))}
              <th className="px-5 py-3">Submitted</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + 2} className="px-5 py-8 text-center text-navy/40">
                  No submissions yet.
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const isEditing = editingId === row.id;
              const isConfirming = confirmingId === row.id;
              const isBusy = busyId === row.id;
              const error = rowError[row.id];
              return (
                <tr key={row.id} className="border-b border-navy/5 last:border-0">
                  {columns.map((col) => (
                    <td key={col.key} className="px-5 py-3 align-top">
                      {isEditing ? (
                        col.options ? (
                          <select
                            className="focus-ring w-full rounded-lg border-2 border-navy/12 px-2 py-1.5 text-sm"
                            value={draft[col.key] ?? ""}
                            onChange={(e) =>
                              setDraft((d) => ({ ...d, [col.key]: e.target.value }))
                            }
                          >
                            {col.options.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={col.type === "number" ? "number" : "text"}
                            className="focus-ring w-full rounded-lg border-2 border-navy/12 px-2 py-1.5 text-sm"
                            value={draft[col.key] ?? ""}
                            onChange={(e) =>
                              setDraft((d) => ({ ...d, [col.key]: e.target.value }))
                            }
                          />
                        )
                      ) : (
                        <span className="text-navy/80">{String(row[col.key] ?? "")}</span>
                      )}
                    </td>
                  ))}
                  <td className="px-5 py-3 whitespace-nowrap text-navy/50">
                    {formatDate(row.createdAt)}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center justify-end gap-2">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => saveEdit(row.id)}
                              className="focus-ring rounded-lg p-2 text-success hover:bg-success/10"
                              aria-label="Save"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={cancelEdit}
                              className="focus-ring rounded-lg p-2 text-navy/50 hover:bg-navy/5"
                              aria-label="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </>
                        ) : isConfirming ? (
                          <>
                            <span className="text-xs font-bold text-red-500">Delete?</span>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => remove(row.id)}
                              className="focus-ring rounded-lg p-2 text-white bg-red-500 hover:bg-red-600"
                              aria-label="Confirm delete"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => setConfirmingId(null)}
                              className="focus-ring rounded-lg p-2 text-navy/50 hover:bg-navy/5"
                              aria-label="Cancel delete"
                            >
                              <X size={16} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => startEdit(row)}
                              className="focus-ring rounded-lg p-2 text-navy/50 hover:bg-navy/5"
                              aria-label="Edit"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => setConfirmingId(row.id)}
                              className="focus-ring rounded-lg p-2 text-red-500 hover:bg-red-50"
                              aria-label="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                      {error && <span className="text-xs font-semibold text-red-600">{error}</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
